import { Paper, Typography } from '@material-ui/core';
import { useMetricsSectionPreference } from '../hooks/useMetricsSectionPreference';
import type {
  CockpitMetricView,
  CockpitRailSegment,
} from '../model/cockpitViewModel';
import { ActivityEvidenceRail } from './ActivityEvidenceRail';
import { TokenDetails } from './MetricDetails';
import { useCockpitStyles } from './cockpitStyles';

export interface MetricsBarSectionProps {
  metrics: CockpitMetricView[];
  rail: CockpitRailSegment[];
  preferenceScope?: string | null;
  selectedRailId: string | null;
  onSelectRail: (nodeId: string) => void;
}

const DETAILS_REGION_ID = 'metrics-details';

interface BarCell {
  id: string;
  label: string;
  value: string;
  unit: string | null;
}

const MetricCell = ({ cell }: { cell: BarCell }) => {
  const classes = useCockpitStyles();
  return (
    <article className={classes.metric} aria-labelledby={`${cell.id}-label`}>
      <Typography
        id={`${cell.id}-label`}
        component="h3"
        className={classes.metricLabel}
      >
        {cell.label}
      </Typography>
      <div className={classes.metricValueRow}>
        <Typography component="p" className={classes.metricValue}>
          {cell.value}
        </Typography>
        {cell.unit && (
          <Typography
            component="span"
            variant="body2"
            className={classes.metricUnit}
          >
            {cell.unit}
          </Typography>
        )}
      </div>
    </article>
  );
};

const MetricsBarHead = ({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) => {
  const classes = useCockpitStyles();
  return (
    <div className={classes.metricsBarHead}>
      <Typography
        id="metrics-title"
        component="h2"
        className={classes.sectionTitle}
      >
        Métricas
      </Typography>
      <button
        type="button"
        className={classes.metricDetailsToggle}
        aria-expanded={expanded}
        aria-controls={DETAILS_REGION_ID}
        onClick={onToggle}
      >
        {expanded ? 'Ocultar detalhes' : 'Ver detalhes'}
      </button>
    </div>
  );
};

const CoverageDetails = ({
  rail,
  selectedRailId,
  onSelectRail,
}: Pick<
  MetricsBarSectionProps,
  'rail' | 'selectedRailId' | 'onSelectRail'
>) => {
  const classes = useCockpitStyles();
  return (
    <section
      className={classes.metricCoverage}
      aria-labelledby="coverage-title"
    >
      <Typography
        id="coverage-title"
        component="h4"
        variant="subtitle2"
        className={classes.metricCoverageTitle}
      >
        Cobertura temporal
      </Typography>
      <Typography variant="body2" className={classes.sectionLede}>
        Largura proporcional à duração observada. Nada é presumido nos
        intervalos sem sinal.
      </Typography>
      <ActivityEvidenceRail
        segments={rail}
        selectedId={selectedRailId}
        onSelect={onSelectRail}
      />
    </section>
  );
};

/**
 * The unclassified time is not repeated here: the coverage rail already
 * reports it as "Sem sinal registrado" with its share of the window.
 */
const MetricsDetails = ({
  metrics,
  rail,
  selectedRailId,
  onSelectRail,
}: Omit<MetricsBarSectionProps, 'preferenceScope'>) => {
  const classes = useCockpitStyles();
  const tokenDetails =
    metricById(metrics, 'input-tokens')?.details ??
    metricById(metrics, 'output-tokens')?.details ??
    null;
  return (
    <div
      id={DETAILS_REGION_ID}
      role="region"
      aria-label="Detalhes das métricas"
      className={classes.metricDetailsRegion}
    >
      {tokenDetails && <TokenDetails details={tokenDetails} />}
      <CoverageDetails
        rail={rail}
        selectedRailId={selectedRailId}
        onSelectRail={onSelectRail}
      />
    </div>
  );
};

/**
 * One line of facts (RN-COCK-02/06): total time, tokens in and out, the
 * reported cost and the model, with no notices: an unavailable value reads
 * "Indisponível" and nothing else is said about the measurement. The token
 * decomposition and the temporal coverage rail live behind "Ver detalhes"
 * (RN-COCK-03).
 */
export const MetricsBarSection = ({
  metrics,
  rail,
  preferenceScope,
  selectedRailId,
  onSelectRail,
}: MetricsBarSectionProps) => {
  const classes = useCockpitStyles();
  const preference = useMetricsSectionPreference(preferenceScope, false);
  return (
    <Paper
      component="section"
      className={classes.panel}
      aria-labelledby="metrics-title"
    >
      <MetricsBarHead
        expanded={preference.expanded}
        onToggle={() => preference.setExpanded(!preference.expanded)}
      />
      <div
        className={classes.metricBar}
        role="group"
        aria-label="Resumo de métricas"
      >
        {barCells(metrics).map(cell => (
          <MetricCell key={cell.id} cell={cell} />
        ))}
      </div>
      {preference.expanded && (
        <MetricsDetails
          metrics={metrics}
          rail={rail}
          selectedRailId={selectedRailId}
          onSelectRail={onSelectRail}
        />
      )}
    </Paper>
  );
};

function metricById(
  metrics: CockpitMetricView[],
  id: string,
): CockpitMetricView | undefined {
  return metrics.find(metric => metric.id === id);
}

function tokenCell(
  id: string,
  label: string,
  metric: CockpitMetricView | undefined,
): BarCell {
  return {
    id,
    label,
    value: metric?.displayValue ?? 'Indisponível',
    unit: metric && metric.rawValue !== null ? metric.unit : null,
  };
}

function barCells(metrics: CockpitMetricView[]): BarCell[] {
  const input = metricById(metrics, 'input-tokens');
  const output = metricById(metrics, 'output-tokens');
  const costs = metrics.filter(
    metric => metric.id.startsWith('cost-') && metric.rawValue !== null,
  );
  return [
    {
      id: 'bar-lead-time',
      label: 'Tempo total',
      value: metricById(metrics, 'lead-time')?.displayValue ?? 'Indisponível',
      unit: null,
    },
    tokenCell('bar-input-tokens', 'Tokens de entrada', input),
    tokenCell('bar-output-tokens', 'Tokens de saída', output),
    {
      id: 'bar-cost',
      label: 'Custo',
      // the cost cell never derives a value: only reported amounts are joined
      value:
        costs.length > 0
          ? costs.map(cost => cost.displayValue).join(' · ')
          : 'Indisponível',
      unit: null,
    },
    {
      id: 'bar-model',
      label: 'Modelo',
      value: modelNames(input ?? output) ?? 'Não informado',
      unit: null,
    },
  ];
}

// Breakdown rows read "model · source"; the bar only names the models, once each.
function modelNames(metric: CockpitMetricView | undefined): string | null {
  const rows = metric?.details?.byModel ?? [];
  const names = Array.from(
    new Set(rows.map(row => row.label.split(' · ')[0].trim())),
  ).filter(name => name.length > 0);
  return names.length > 0 ? names.join(', ') : null;
}
